<template>
  <div class="my-4" v-if="dataSet != null || Object.keys(dataSet).length > 0">
    <div v-for="(k, i) in Object.keys(getData.fieldsComposer)" :key="i" class="mb-3 px-2">
      <data-row :item="dataSet[k]" :currency="currency"></data-row>
      <div v-if="'border' in dataSet[k] && dataSet[k].border" class="my-3 data-row-border-bottom"></div>
    </div>
  </div>
</template>

<script>
import DataRow from './DataRow'
import {
  VISIBILITY_RULES,
  FIELDS_COMPOSER,
  DATE_VALUES,
  MONETARY_VALUES,
  PERCENTAGE_VALUES,
  SLIDER_PERCENTAGE,
  YEARS_VALUES,
} from '../../dip/configuration'
import { formatShortDate, formatCurrency, formatPercentage, rhpToYears } from '../../utils/formatter'

export default {
  components: {
    DataRow,
  },

  props: {
    data: {
      type: Object,
      requred: true,
    },
    currency: {
      type: String,
    },
  },

  computed: {
    getData: {
      get() {
        const visibilityRules = VISIBILITY_RULES()
        const productInfo = this.data.productInfo
        let fieldsComposer = Object.assign({}, FIELDS_COMPOSER()[0])
        Object.keys(fieldsComposer).forEach((key) => {
          const arrValues = visibilityRules.get(key)
          if (Array.isArray(arrValues) && arrValues.length > 0) {
            const match = arrValues.find((o) => o.key in productInfo && productInfo[o.key] === o.value)
            if (!match) delete fieldsComposer[key]
          }
        })
        return {
          additionalInfo: this.data.additionalInfo,
          fieldsComposer: fieldsComposer,
        }
      },
    },

    dataSet: {
      get() {
        let out = {}
        const data = this.getData.additionalInfo
        const fields = this.getData.fieldsComposer

        Object.keys(fields).forEach((k) => {
          out[k] = {
            component: fields[k].component || null,
            border: fields[k].border || false,
            key: this.$i18n.messages[this.$i18n.locale].productDetails.DIP.additionalInformation[k] || null,
            value: data ? this.formatValue(k, data[k]) : null,
          }

          if ('isValueOf' in fields[k] && data) {
            out[k]['value'] =
              typeof data[k] === 'number'
                ? this.$i18n.messages[this.$i18n.locale].productDetails.DIP[fields[k].isValueOf][data[k]]
                : null
          }

          if (fields[k].component === 'minMax') {
            const minValue = data ? this.formatValue(k, data[k + 'Min']) : null
            const maxValue = data ? this.formatValue(k, data[k + 'Max']) : null

            if (minValue === maxValue) {
              out[k]['component'] = 'default'
              out[k]['value'] = minValue
            } else {
              out[k]['value'] = {
                min: this.formatValue(k, data[k + 'Min']) || null,
                max: this.formatValue(k, data[k + 'Max']) || null,
              }
            }
          }

          if (fields[k].component === 'CustomSlider' && data) {
            let min = data[k + 'Min'] ? data[k + 'Min'] : null
            let max = data[k + 'Max'] ? data[k + 'Max'] : null

            if (fields[k].marks) {
              min = fields[k].marks.inf > min ? null : this.formatValue(k, min)
              max = fields[k].marks.sup < max ? null : this.formatValue(k, max)
            }

            out[k]['value'] = min || max ? [min, max] : null
            out[k]['value'] = min > max ? null : out[k]['value']
            out[k]['formatter'] = fields[k].formatter || null
            out[k]['marks'] = fields[k].marks || null
          }

          if ('options' in fields[k]) {
            out[k]['options'] = []
            delete out[k]['value']
            const options = fields[k].options
            options.forEach((item) => {
              out[k].options.push({
                ...item,
                key: this.$i18n.messages[this.$i18n.locale].productDetails.DIP.additionalInformation[item.key],
                value: data ? data[item.key] : null,
                legend:
                  'legend' in item
                    ? this.$i18n.messages[this.$i18n.locale].productDetails.DIP.additionalInformation[item.legend]
                    : null,
                legendValue: data && 'legend' in item ? this.formatValue(item.legend, data[item.legend]) : null,
              })
            })
          }

          if ('enumOptions' in fields[k]) {
            out[k]['options'] = []
            delete out[k]['value']
            const options = fields[k].enumOptions
            const badgeClasses = fields[k].badge_classes
            options.forEach((item) => {
              let value = data && typeof data[item.key] === 'number'
              let badgeClass = value && badgeClasses ? badgeClasses[data[item.key]] : null
              out[k].options.push({
                ...item,
                key: this.$i18n.messages[this.$i18n.locale].productDetails.DIP.additionalInformation[item.key],
                value: value,
                badge: value && badgeClass,
                badge_class: badgeClass,
              })
            })
          }

          if ('valueDict' in fields[k] && data) {
            out[k]['options'] = []
            delete out[k]['value']
            const options = fields[k].options
            const dict = fields[k].valueDict
            const value = data && typeof data[k] === 'number' ? data[k] : null
            options.forEach((item) => {
              out[k].options.push({
                ...item,
                key: this.$i18n.messages[this.$i18n.locale].productDetails.DIP.additionalInformation[item.key],
                value: dict[item.key].includes(value),
              })
            })
          }

          if ('tableFields' in fields[k] && data && data[k]) {
            out[k]['fields'] = []
            out[k]['options'] = []
            const existanceCondition = 'existsIfTrue' in fields[k] && data[fields[k].existsIfTrue]

            if ((Array.isArray(data[k]) && data[k].length === 0) || !existanceCondition) {
              delete out[k]['options']
            } else {
              let tableRows = data[k]
              const tableFields = fields[k].tableFields

              const hideNullValues = 'hideNullValues' in fields[k] && fields[k].hideNullValues
              if (hideNullValues === true) {
                tableRows = tableRows.filter((el) => el.value != null)
              }

              tableRows.forEach((item) => {
                let o = {}
                const k1 = this.$i18n.messages[this.$i18n.locale].common[tableFields[0]]
                const k2 = this.$i18n.messages[this.$i18n.locale].common[tableFields[1]]
                if (item.from !== item.to) {
                  const from =
                    typeof item.from === 'number'
                      ? this.$i18n.messages[this.$i18n.locale].common['from'] + ' ' + item.from
                      : ''
                  const to =
                    typeof item.to === 'number'
                      ? this.$i18n.messages[this.$i18n.locale].common['to'] + ' ' + item.to
                      : this.$i18n.messages[this.$i18n.locale].common['onwards']
                  o[k1] = from + ' ' + to
                } else {
                  o[k1] = item.from
                }
                o[k2] =
                  typeof item.value === 'number'
                    ? item.value
                    : this.$i18n.messages[this.$i18n.locale].common['notAvailable']
                o[k2] = typeof o[k2] === 'number' ? this.formatValue(k, o[k2]) : o[k2]
                out[k]['fields'].push(o)
                out[k]['options'].push({ key: o[k1], value: o[k2] })
              })
            }
          }
        })
        return out
      },
    },
  },

  methods: {
    formatValue(key, value) {
      if (DATE_VALUES.includes(key)) {
        return formatShortDate(value)
      }

      if (MONETARY_VALUES.includes(key)) {
        return formatCurrency(value, this.currency)
      }

      if (PERCENTAGE_VALUES.includes(key)) {
        return formatPercentage(value)
      }

      if (SLIDER_PERCENTAGE.includes(key)) {
        return value * 100
      }

      if (YEARS_VALUES.includes(key)) {
        if (value == null) {
          return null
        }
        return rhpToYears(value)
      }

      return value
    },
  },
}
</script>

<style lang="scss" scoped>
.data-row-border-bottom {
  border-bottom: 2px solid #dee2e6 !important;
}
</style>
